/**
 * Ad effective-status vocabulary, shared by the Overview and Drilldown filter bars.
 *
 * The two dashboard modes read different BigQuery tables and therefore have genuinely
 * different domains — do not merge them:
 *
 * - My Brand (`core.new_feature_table_merged`) stores Meta's enum. A census of the column
 *   returns 17 distinct values, but only the seven below are exposed, matching Retool's
 *   dropdown. The rest are either pipeline noise (`0`, `In-Flight`, `DISABLE`) or absent
 *   for most tenants (`NOT_ELIGIBLE`, `DELETED`, `REMOVED`, `LIMITED`).
 * - Competitors (`trump.competitor_raw_table`) stores only lowercase `active` / `inactive`.
 *
 * Casing in the My Brand column is genuinely mixed (both `ACTIVE` and `active` occur), so
 * the backend must keep comparing case-insensitively; the uppercase spelling here mirrors
 * the dominant BQ enum for readability.
 */

export interface AdStatusOption {
  label: string;
  value: string;
}

/** Competitors mode — the competitor table only ever holds these two values. */
export const COMPETITOR_EFFECTIVE_STATUS_OPTIONS: readonly AdStatusOption[] = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
] as const;

/** My Brand mode — Retool's exposed status set, in Retool's own order. */
export const MY_BRAND_EFFECTIVE_STATUS_OPTIONS: readonly AdStatusOption[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Adset Paused", value: "ADSET_PAUSED" },
  { label: "Archived", value: "ARCHIVED" },
  { label: "Campaign Paused", value: "CAMPAIGN_PAUSED" },
  { label: "Disapproved", value: "DISAPPROVED" },
  { label: "Paused", value: "PAUSED" },
  { label: "With Issues", value: "WITH_ISSUES" },
] as const;

/**
 * Retool's "No Active Ads" / Drilldown's "Inactive Ads" quick-select: every exposed status
 * except ACTIVE.
 *
 * Deliberately a derived set rather than the literal `inactive` value that also exists in
 * the column. Most tenants have no `inactive` rows at all (tpj and integra_beauty have
 * zero), so binding the option to that value would render it permanently empty.
 */
export const MY_BRAND_INACTIVE_STATUSES: readonly string[] = MY_BRAND_EFFECTIVE_STATUS_OPTIONS
  .filter((o) => o.value !== "ACTIVE")
  .map((o) => o.value);

/** Empty selection means "no filter"; both modes render this on the closed pill. */
export const ALL_AD_STATUSES_LABEL = "All Ad Statuses";
